// Filters that live on the shared route view but must NOT be forwarded to a server-side
// count/groupBy aggregate request:
//   - paged: paging is meaningless for an aggregate and would cap the count.
//   - forTempQueryResult: the #332 Go-Fetch display gate ({ _id: { $in } }); it constrains the
//     view to one fetch's results and would wrongly shrink the count (the grid strips it too).
//   - fullTextSearch: getQueryUrl routes this to the `/search` endpoint, which collides with the
//     `/aggregate` endpoint the count needs; full-text counts are handled separately.
//   - caseSensitive / alsoSearchWithAscii / exactMatchOnly / propertiesToSearch: full-text-search
//     companions of `fullTextSearch`. caseSensitive and alsoSearchWithAscii getUriComponent() throw
//     'not implemented' (so forwarding them breaks the aggregate request on routes with FTS options),
//     and exactMatchOnly/propertiesToSearch only mean anything alongside fullTextSearch, which we drop.
//   - orderBy / orderByMultiple: sorting has no effect on a count and just bloats the URL.
//   - aggregateType / groupBy / groupByBoolean: the widget supplies its own via metadataBasedFilters;
//     a stray copy off the shared view must not override it.
const EXCLUDED_VIEW_FILTERS = new Set([
    'paged',
    'forTempQueryResult',
    'fullTextSearch',
    'caseSensitive',
    'alsoSearchWithAscii',
    'exactMatchOnly',
    'propertiesToSearch',
    'orderBy',
    'orderByMultiple',
    'aggregateType',
    'groupBy',
    'groupByBoolean'
]);

/**
 * Build the requestFilters for a Go-Fetch (limitAllSyncSizes) server aggregation so the count /
 * chart matches what the grid on the same page queries.
 *
 * Every component on a route shares one LokiJS view; the grid scopes that view to the selected
 * inventory (a `nestedListEntry` membership filter), plus excludeDeleted, includedValues, any
 * searchCriteria / navigation-selection criteria, etc. The grid forwards the full
 * getFiltersFromView() set to getQueryUrl, so it is correctly scoped. The aggregate widget must do
 * the same — previously it only forwarded `searchCriteria`, so the count ignored the inventory and
 * returned every asset (#395 — Inventory > Count "Records" showed all assets until a location was
 * picked). We mirror the grid: forward all shared-view find-filters except the few that don't
 * translate to a count (see EXCLUDED_VIEW_FILTERS). getQueryUrl already knows how to serialize each
 * of these into the `criteria` query param.
 *
 * @param {object} metadataBasedFilters - the widget's own hNode filter children (win on conflict).
 * @param {object} sharedViewFilters - getFiltersFromView() output from the shared route view.
 * @returns {object} requestFilters suitable for useServerAggregation / getQueryUrl.
 */
export default function buildServerAggregationFilters(metadataBasedFilters = {}, sharedViewFilters = {}) {
    const forwardable = {};
    for (const [filterName, filterValue] of Object.entries(sharedViewFilters)) {
        if (EXCLUDED_VIEW_FILTERS.has(filterName)) continue;
        if (filterValue == null) continue;
        forwardable[filterName] = filterValue;
    }
    // Page-level scoping from the shared view first, then the widget's own hNode filters so an
    // explicit per-widget filter wins; aggregateType is always count for these consumers.
    return {
        ...forwardable,
        ...metadataBasedFilters,
        aggregateType: { aggregateType: 'count' }
    };
}

export const _excludedViewFilters = EXCLUDED_VIEW_FILTERS;
